<template>
  <div class="usage-statistics-chart">
    <a-row :gutter="16">
      <a-col :span="24">
        <a-card :title="$t('usageManagement.statisticsChart')" size="small">
          <template #extra>
            <a-space>
              <a-select
                v-model:value="selectedGroupId"
                :placeholder="$t('usageManagement.selectUserGroup')"
                style="width: 200px"
                allow-clear
                @change="loadStatistics"
              >
                <a-select-option v-for="group in groupOptions" :key="group.id" :value="group.id">
                  {{ group.name }}
                </a-select-option>
              </a-select>
              <a-range-picker
                v-model:value="dateRange"
                @change="loadStatistics"
              />
              <a-button @click="loadStatistics">
                <ReloadOutlined />
                {{ $t('usageManagement.refresh') }}
              </a-button>
            </a-space>
          </template>

          <a-spin :spinning="loading">
            <div v-if="!hasData" class="no-data">
              <a-empty :description="$t('usageManagement.noData')" />
            </div>
            <div v-else>
              <!-- 总体统计卡片 -->
              <a-row :gutter="16" class="mb-4">
                <a-col :span="8">
                  <a-card size="small">
                    <a-statistic
                      :title="$t('usageManagement.totalCharacterUsage')"
                      :value="statistics.total_character_usage"
                      :suffix="$t('usageManagement.characters')"
                      :value-style="{ color: '#1890ff' }"
                    />
                  </a-card>
                </a-col>
                <a-col :span="8">
                  <a-card size="small">
                    <a-statistic
                      :title="$t('usageManagement.totalTimeUsage')"
                      :value="Math.round(statistics.total_time_usage / 60)"
                      :suffix="$t('common.minutes')"
                      :value-style="{ color: '#52c41a' }"
                    />
                  </a-card>
                </a-col>
                <a-col :span="8">
                  <a-card size="small">
                    <a-statistic
                      :title="$t('usageManagement.statisticsPeriod')"
                      :value="statisticsPeriod"
                      :value-style="{ color: '#722ed1' }"
                    />
                  </a-card>
                </a-col>
              </a-row>

              <!-- 服务类型分布图 -->
              <a-row :gutter="16" class="mb-4">
                <a-col :span="12">
                  <a-card :title="$t('usageManagement.characterUsageDistribution')" size="small">
                    <div ref="characterChartRef" style="height: 300px;"></div>
                  </a-card>
                </a-col>
                <a-col :span="12">
                  <a-card :title="$t('usageManagement.timeUsageDistribution')" size="small">
                    <div ref="timeChartRef" style="height: 300px;"></div>
                  </a-card>
                </a-col>
              </a-row>

              <!-- 用量趋势图 -->
              <a-row :gutter="16">
                <a-col :span="24">
                  <a-card :title="$t('usageManagement.usageTrend')" size="small">
                    <div ref="trendChartRef" style="height: 400px;"></div>
                  </a-card>
                </a-col>
              </a-row>
            </div>
          </a-spin>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watch, nextTick, computed, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { ReloadOutlined } from '@ant-design/icons-vue'
import * as echarts from 'echarts'
import { usageApi } from '@/modules/usage/api'
import { getUserGroups } from '@/modules/user/api'
import type { ECharts } from 'echarts'
import dayjs from 'dayjs'

const { t, locale } = useI18n()

interface Props {
  groupId?: number
}

const props = withDefaults(defineProps<Props>(), {
  groupId: undefined,
})

// 响应式数据
const loading = ref(false)
const selectedGroupId = ref<number | undefined>(props.groupId)
const dateRange = ref<any>([])
const groupOptions = ref<any[]>([])
const statistics = ref<any>({
  total_character_usage: 0,
  total_time_usage: 0,
  service_breakdown: {},
})

// 图表引用
const characterChartRef = ref<HTMLDivElement>()
const timeChartRef = ref<HTMLDivElement>()
const trendChartRef = ref<HTMLDivElement>()

// 图表实例
let characterChart: ECharts | null = null
let timeChart: ECharts | null = null
let trendChart: ECharts | null = null

// 计算属性
const hasData = computed(() => {
  const hasCharacter = statistics.value.total_character_usage > 0
  const hasTime = statistics.value.total_time_usage > 0
  return hasCharacter || hasTime
})

const statisticsPeriod = computed(() => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    return t('usageManagement.last30Days')
  }
  const start = dateRange.value[0].format('MM-DD')
  const end = dateRange.value[1].format('MM-DD')
  return t('usageManagement.dateRangeFormat', { start, end })
})

// 监听props变化
watch(() => props.groupId, (newGroupId) => {
  selectedGroupId.value = newGroupId
  loadStatistics()
})

// 方法
const loadGroupOptions = async () => {
  try {
    const response = await getUserGroups()
    groupOptions.value = response
  } catch (error) {
    console.error('Failed to load user groups:', error)
  }
}

const loadStatistics = async () => {
  loading.value = true
  try {
    const params: any = {}
    
    if (selectedGroupId.value) {
      params.group_id = selectedGroupId.value
    }
    
    // 如果没有选择日期范围，默认使用最近30天
    if (dateRange.value && dateRange.value.length === 2) {
      params.start_date = dateRange.value[0].toISOString()
      params.end_date = dateRange.value[1].toISOString()
    } else {
      // 默认最近30天
      const endDate = dayjs()
      const startDate = dayjs().subtract(30, 'day')
      params.start_date = startDate.toISOString()
      params.end_date = endDate.toISOString()
    }
    
    const response = await usageApi.getUsageStatistics(params)
    statistics.value = response || {
      total_character_usage: 0,
      total_time_usage: 0,
      service_breakdown: {},
    }
    
    await nextTick()
    renderCharts()
  } catch (error) {
    console.error('Failed to load statistics:', error)
  } finally {
    loading.value = false
  }
}

const renderCharts = () => {
  renderCharacterChart()
  renderTimeChart()
  renderTrendChart()
}

const renderCharacterChart = () => {
  if (!characterChartRef.value) return
  
  if (characterChart) {
    characterChart.dispose()
  }
  
  characterChart = echarts.init(characterChartRef.value)
  
  const serviceBreakdown = statistics.value.service_breakdown || {}
  const data = Object.entries(serviceBreakdown).map(([service, usage]: [string, any]) => ({
    name: getServiceName(service),
    value: usage.character_usage || 0,
  })).filter(item => item.value > 0)
  
  const characterUsageText = t('usageManagement.characterUsage')
  const charactersText = t('usageManagement.characters')
  
  const option = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        return `${params.seriesName}<br/>${params.name}: ${params.value} ${charactersText} (${params.percent}%)`
      }
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [
      {
        name: characterUsageText,
        type: 'pie',
        radius: '50%',
        data: data,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  }
  
  characterChart.setOption(option)
}

const renderTimeChart = () => {
  if (!timeChartRef.value) return
  
  if (timeChart) {
    timeChart.dispose()
  }
  
  timeChart = echarts.init(timeChartRef.value)
  
  const serviceBreakdown = statistics.value.service_breakdown || {}
  const data = Object.entries(serviceBreakdown).map(([service, usage]: [string, any]) => ({
    name: getServiceName(service),
    value: Math.round((usage.time_usage || 0) / 60), // 转换为分钟
  })).filter(item => item.value > 0)
  
  const timeUsageText = t('usageManagement.timeUsage')
  const minutesText = t('common.minutes')
  
  const option = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        return `${params.seriesName}<br/>${params.name}: ${params.value} ${minutesText} (${params.percent}%)`
      }
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [
      {
        name: timeUsageText,
        type: 'pie',
        radius: '50%',
        data: data,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  }
  
  timeChart.setOption(option)
}

const renderTrendChart = () => {
  if (!trendChartRef.value) return
  
  if (trendChart) {
    trendChart.dispose()
  }
  
  trendChart = echarts.init(trendChartRef.value)
  
  // 使用真实的统计数据
  const serviceBreakdown = statistics.value.service_breakdown || {}
  
  // 从统计数据中提取字符和时间用量
  let totalCharacterUsage = 0
  let totalTimeUsage = 0
  
  Object.values(serviceBreakdown).forEach((usage: any) => {
    totalCharacterUsage += Number(usage.character_usage) || 0
    totalTimeUsage += Number(usage.time_usage) || 0
  })
  
  // 生成趋势图数据，使用后端按日期聚合的实际数据
  const dates = []
  const characterData = []
  const timeData = []
  
  // 获取后端返回的按日期聚合数据
  const dailyBreakdown = statistics.value.daily_breakdown || {}
  
  // 根据统计期间生成日期范围
  const periodStart = statistics.value.period_start ? new Date(statistics.value.period_start) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  const periodEnd = statistics.value.period_end ? new Date(statistics.value.period_end) : new Date()
  
  // 计算天数
  const daysDiff = Math.ceil((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24))
  const daysToShow = Math.min(daysDiff, 30) // 最多显示30天
  
  // 生成日期数据
  const dateLocale = locale.value === 'zh' ? 'zh-CN' : 'en-US'
  for (let i = daysToShow - 1; i >= 0; i--) {
    const date = new Date(periodEnd)
    date.setDate(date.getDate() - i)
    const dateStr = date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
    const dateKey = date.toISOString().split('T')[0] // YYYY-MM-DD格式
    
    dates.push(dateStr)
    
    // 使用后端按日期聚合的实际数据
    const dailyData = dailyBreakdown[dateKey] || { character_usage: 0, time_usage: 0 }
    const characterValue = dailyData.character_usage || 0
    const timeValue = dailyData.time_usage || 0
    
    characterData.push(isNaN(characterValue) ? 0 : characterValue)
    timeData.push(isNaN(timeValue) ? 0 : timeValue)
  }
  
  
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      },
      formatter: function (params: any) {
        const characterUsageText = t('usageManagement.characterUsage')
        const timeUsageText = t('usageManagement.timeUsage')
        const charactersText = t('usageManagement.characters')
        const minutesText = t('common.minutes')
        
        let result = params[0].name + '<br/>'
        params.forEach((param: any) => {
          let value = ''
          if (param.seriesName === timeUsageText) {
            // 时间用量已经是分钟，直接显示
            value = param.value + ' ' + minutesText
          } else {
            // 字符用量显示字符数
            value = param.value + ' ' + charactersText
          }
          result += `${param.marker}${param.seriesName}: ${value}<br/>`
        })
        return result
      }
    },
    legend: {
      data: [t('usageManagement.characterUsage'), t('usageManagement.timeUsage')]
    },
    toolbox: {
      feature: {
        saveAsImage: {}
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        data: dates
      }
    ],
    yAxis: [
      {
        type: 'value',
        name: t('usageManagement.characterCount'),
        position: 'left',
        axisLabel: {
          formatter: '{value}'
        }
      },
      {
        type: 'value',
        name: t('common.minutes'),
        position: 'right',
        axisLabel: {
          formatter: '{value}'
        }
      }
    ],
    series: [
      {
        name: t('usageManagement.characterUsage'),
        type: 'line',
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
              { offset: 1, color: 'rgba(24, 144, 255, 0.1)' }
            ]
          }
        },
        emphasis: {
          focus: 'series'
        },
        data: characterData,
        yAxisIndex: 0,
        itemStyle: {
          color: '#1890ff'
        }
      },
      {
        name: t('usageManagement.timeUsage'),
        type: 'line',
        emphasis: {
          focus: 'series'
        },
        data: timeData, // 时间数据已经是分钟
        yAxisIndex: 1,
        itemStyle: {
          color: '#52c41a'
        },
        lineStyle: {
          width: 2
        },
        symbol: 'circle',
        symbolSize: 6
      }
    ]
  }
  
  trendChart.setOption(option)
}

const getServiceName = (service: string) => {
  const names: Record<string, string> = {
    video_translation: t('usage.videoTranslation'),
    document_translation: t('usage.documentTranslation'),
    message_translation: t('usage.messageTranslation'),
    hot_word_extraction: t('usage.hotWordExtraction'),
    speech_translation: t('usage.speechTranslation'),
  }
  return names[service] || service
}

// 响应式处理
const handleResize = () => {
  characterChart?.resize()
  timeChart?.resize()
  trendChart?.resize()
}

// 生命周期
onMounted(async () => {
  await loadGroupOptions()
  await loadStatistics()
  
  window.addEventListener('resize', handleResize)
})

// 清理
const cleanup = () => {
  window.removeEventListener('resize', handleResize)
  characterChart?.dispose()
  timeChart?.dispose()
  trendChart?.dispose()
}

// 组件卸载时清理
onBeforeUnmount(cleanup)
</script>

<style scoped>
.usage-statistics-chart {
  width: 100%;
}

.mb-4 {
  margin-bottom: 16px;
}

.no-data {
  text-align: center;
  padding: 40px 0;
}
</style>

